"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Druid_1 = require("../../Druid");
const Selectors_1 = require("../../Utility/Selectors");
/**
 * Add automatic form validation to a form.
 * - will color the border red on change/blur of invalid input element
 * - allows registration for custom validation
 * - allows to invoke reportValidation to show the user incorrect input elements
 */
class FormValidator {
    constructor(el, options) {
        this.customValidations = [];
        this.el = el;
        this.options = options !== null && options !== void 0 ? options : {};
        this.formElements = Druid_1.default.configuration().form.elements.join(',');
        this.invalidInputClass = Druid_1.default.configuration().form.invalidInputClass;
        if (this.el) {
            this.attachEvents(this.el);
        }
    }
    setAccessor(accessor) {
        accessor.onMounted(() => {
            this.el = accessor.getElement();
            this.attachEvents(this.el);
        });
    }
    /**
     * Add a custom validation rule next to the html5 defined required/pattern rules
     */
    registerValidationConstraint(validation) {
        this.customValidations.push(validation);
        return this;
    }
    /**
     * Run all validation checks and report any problems to the user using browser native report popup
     */
    reportValidity() {
        var _a, _b;
        if (this.el === undefined) {
            return true;
        }
        let valid = true;
        const dataRole = (_a = Selectors_1.default.createDataRoleSelector(this.options.role)) !== null && _a !== void 0 ? _a : '';
        const prefix = (_b = this.options.selectorPrefix) !== null && _b !== void 0 ? _b : '';
        const selector = Druid_1.default.configuration().form.elements.map((val) => prefix + val + dataRole).join(',');
        this.el.querySelectorAll(selector).forEach((element) => {
            // check validity of all elements, report only for the first failing element
            if (this.checkValidity(element, valid) === false) {
                valid = false;
            }
        });
        return valid;
    }
    checkValidity(element, report) {
        const name = element.getAttribute('name');
        if ((name === null || name === '') && this.options.includeNameless !== true) {
            return true;
        }
        let valid = FormValidator.checkHtml5Validity(element, report);
        // run custom validation rules while state is valid
        this.customValidations.forEach((validation) => {
            if (valid) {
                valid = report ? validation.reportValidity(element) : validation.checkValidity(element);
            }
        });
        // set error indicator on input field
        if (valid) {
            element.classList.remove(this.invalidInputClass);
        }
        else {
            element.classList.add(this.invalidInputClass);
        }
        return valid;
    }
    /**
     * Mark all fields with the give `name` attribute as invalid
     */
    setInvalid(elementNames) {
        if (this.el === undefined) {
            return;
        }
        // clear any already invalid fields
        this.el.querySelectorAll('.dr-form--invalid-input').forEach(el => el.classList.remove(this.invalidInputClass));
        // update new invalid fields
        for (const elementName of elementNames) {
            void this.el.queryPromise(`[name="${elementName}"]`).then(el => el.classList.add(this.invalidInputClass));
        }
    }
    attachEvents(el) {
        // check validity when a form element is changed
        el.addDelegateListener('change', this.formElements, event => this.checkValidity(event.delegateTarget, false));
        // recheck validity when data is changed (only if it was invalid before)
        el.addDelegateListener('input', '.' + this.invalidInputClass, event => this.checkValidity(event.delegateTarget, false));
        if (this.options.validateOnBlur === true) {
            el.addDelegateListener('blur focusout', this.formElements, event => this.checkValidity(event.delegateTarget, false));
        }
    }
    /**
     * Set a custom validity message, and reset on input
     */
    static setCustomValidityMessage(element, message) {
        // set message
        element.setCustomValidity(message);
        // create input listener to reset message
        element.addEventListener('input', () => element.setCustomValidity(''), { once: true });
    }
    /**
     * Set the required validity message
     */
    static setCustomRequiredMessage(valid, report, element, required) {
        var _a;
        if (valid === false && report) {
            if ((_a = element.hasCustomValidityMessage) === null || _a === void 0 ? void 0 : _a.call(element)) {
                // already has a custom validity message
            }
            else if (required && (element.value.trim() === '' || element.type === 'radio' || element.type === 'checkbox')) {
                FormValidator.setCustomValidityMessage(element, Druid_1.default.configuration().translations.mandatoryField);
            }
            else if (element.dataset.invalidMessage) {
                FormValidator.setCustomValidityMessage(element, element.dataset.invalidMessage);
            }
            else if (element.value.trim() !== '') {
                FormValidator.setCustomValidityMessage(element, Druid_1.default.configuration().translations.errorInputInvalid);
            }
        }
    }
    /**
     * Check validity using native html5 functionality
     */
    static checkHtml5Validity(element, report) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (element.reportValidity === undefined) {
            return true;
        }
        // clear any current messages
        element.setCustomValidity('');
        const required = element.hasAttribute('required');
        const pattern = element.getAttribute('pattern');
        // the required attribute still allows whitespace only input. Make sure at least _some_ input is set
        if (required && pattern === null) {
            element.setAttribute('pattern', '.*\\S+.*');
        }
        const valid = report ? element.reportValidity() : element.checkValidity();
        // remove the pattern attribute again for required non whitespace only
        if (required && pattern === null) {
            element.removeAttribute('pattern');
        }
        // set custom required message
        FormValidator.setCustomRequiredMessage(valid, report, element, required);
        return valid;
    }
}
exports.default = FormValidator;
//# sourceMappingURL=FormValidator.js.map